import * as React from "react"
import { Dialog as BaseDialog } from "@base-ui/react/dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import { cn } from "@lib/cn"

const Root = BaseDialog.Root
const Trigger = BaseDialog.Trigger
const Portal = BaseDialog.Portal
const Close = BaseDialog.Close

const Backdrop = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Backdrop>
>(({ className, ...props }, ref) => (
  <BaseDialog.Backdrop
    ref={ref}
    className={cn(
      "fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]",
      "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
      "transition-opacity duration-regular ease-out-quad",
      className,
    )}
    {...props}
  />
))
Backdrop.displayName = "Dialog.Backdrop"

const contentVariants = cva(
  [
    "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
    "flex max-h-[85vh] w-[calc(100vw-2rem)] flex-col overflow-hidden",
    "rounded-xl border bg-white dark:bg-cloverhound-gray-900",
    "border-cloverhound-gray-250 dark:border-cloverhound-gray-800",
    "shadow-xl",
    "data-[starting-style]:opacity-0 data-[starting-style]:scale-95",
    "data-[ending-style]:opacity-0 data-[ending-style]:scale-95",
    "transition-all duration-regular ease-out-quad",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "max-w-md",
        md: "max-w-[40rem]",
        lg: "max-w-3xl",
        xl: "max-w-5xl",
        full: "max-w-[calc(100vw-2rem)]",
      },
    },
    defaultVariants: { size: "md" },
  },
)

export interface ContentProps
  extends React.ComponentPropsWithoutRef<typeof BaseDialog.Popup>,
    VariantProps<typeof contentVariants> {
  withBackdrop?: boolean
  hideClose?: boolean
}

const Content = React.forwardRef<HTMLDivElement, ContentProps>(
  ({ className, size, withBackdrop = true, hideClose = false, children, ...props }, ref) => (
    <BaseDialog.Portal>
      {withBackdrop && <Backdrop />}
      <BaseDialog.Popup ref={ref} className={cn(contentVariants({ size }), className)} {...props}>
        {children}
        {!hideClose && (
          <BaseDialog.Close
            className={cn(
              "absolute right-3 top-3 inline-flex size-7 items-center justify-center rounded-md",
              "text-cloverhound-gray-700 hover:text-cloverhound-gray-900 dark:text-cloverhound-gray-400 dark:hover:text-cloverhound-gray-50",
              "hover:bg-cloverhound-gray-100 dark:hover:bg-cloverhound-gray-800",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500",
            )}
            aria-label="Close dialog"
          >
            <X className="size-4" aria-hidden="true" />
          </BaseDialog.Close>
        )}
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  ),
)
Content.displayName = "Dialog.Content"

const Header = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-1 px-6 pt-6 pb-4", className)} {...props} />
  ),
)
Header.displayName = "Dialog.Header"

const Title = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Title>
>(({ className, ...props }, ref) => (
  <BaseDialog.Title
    ref={ref}
    className={cn(
      "text-large font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50",
      className,
    )}
    {...props}
  />
))
Title.displayName = "Dialog.Title"

const Description = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Description>
>(({ className, ...props }, ref) => (
  <BaseDialog.Description
    ref={ref}
    className={cn("text-small text-cloverhound-gray-700 dark:text-cloverhound-gray-400", className)}
    {...props}
  />
))
Description.displayName = "Dialog.Description"

const Body = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex-1 overflow-y-auto px-6 py-2", className)} {...props} />
  ),
)
Body.displayName = "Dialog.Body"

const Footer = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center justify-end gap-2 px-6 pb-6 pt-4",
        "border-t border-cloverhound-gray-250 dark:border-cloverhound-gray-800",
        className,
      )}
      {...props}
    />
  ),
)
Footer.displayName = "Dialog.Footer"

export const Dialog = { Root, Trigger, Portal, Backdrop, Content, Header, Title, Description, Body, Footer, Close }
